import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PHONE = '+33674626368';
const PHONE_DISPLAY = '+33 6 74 62 63 68';
const EMAIL = 'taxiyellowcabcorsica@gmail.com';
const WHATSAPP_URL = 'https://wa.me/33674626368?text=Bonjour%2C%20je%20souhaite%20r%C3%A9server%20un%20taxi%20Yellow%20Cab%20Corsica.';
const INSTAGRAM_URL = 'https://www.instagram.com/taxisyellowcabcorsica';
const MAPS_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d48695.14!2d8.73897!3d41.91938!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12dd4b8c1a19d98f%3A0x4a2de6e3e7e23cf!2sAjaccio%2C%20Corse-du-Sud!5e0!3m2!1sfr!2sfr!4v1717619000000!5m2!1sfr!2sfr';

const Footer = () => {
  const { t } = useTranslation();
  const navLinks = t('footer_nav', { returnObjects: true });
  const zoneLinks = [
    { label: 'Taxi Aéroport Ajaccio', href: '/taxi-aeroport-ajaccio' },
    { label: 'Taxi Porticcio', href: '/taxi-porticcio' },
    { label: 'Chauffeur Privé Corse-du-Sud', href: '/chauffeur-prive-corse-du-sud' },
    { label: "Transfert Port d'Ajaccio", href: '/transfert-port-ajaccio' },
    { label: 'Blog & Conseils', href: '/blog' },
  ];

  return (
    <footer id="contact" className="bg-[#0A0A0A] border-t border-white/8" data-testid="footer">
      {/* Google Maps */}
      <div className="w-full h-60 relative">
        <iframe
          title="Zone desservie - Porticcio, Rive Sud du Golfe d'Ajaccio"
          src={MAPS_EMBED}
          width="100%" height="100%"
          style={{ border: 0, filter: 'grayscale(40%) invert(10%)' }}
          allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          data-testid="maps-embed"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">

          {/* Brand */}
          <div>
            <div className="mb-5 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              <img src="/logo_icon.png" alt="Yellow Cab Corsica" className="h-14 w-14 object-contain" />
              <span className="font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-[#EAB308]">Yellow</span>
                <span className="text-white"> Cab</span>
                <span className="text-[#A3A3A3] font-light"> Corsica</span>
              </span>
            </div>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-5">{t('footer.desc')}</p>
            <div className="flex gap-3">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/8 text-[#A3A3A3] hover:text-white hover:border-white/20 transition-all" aria-label="Instagram">
                <Instagram size={18} />
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/8 text-[#A3A3A3] hover:text-[#25D366] hover:border-[#25D366]/20 transition-all" aria-label="WhatsApp">
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('footer.nav_title')}</h4>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="text-[#A3A3A3] hover:text-white text-sm transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular routes (SEO internal links) */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('footer.zones_title')}</h4>
            <ul className="space-y-3">
              {zoneLinks.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="text-[#A3A3A3] hover:text-white text-sm transition-colors" data-testid={`footer-zone-${l.href.replace(/\//g, '')}`}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('footer.contact_title')}</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${PHONE}`} data-testid="footer-phone"
                  className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors group">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/15 group-hover:bg-[#EAB308]/15 transition-colors shrink-0">
                    <Phone size={15} className="text-[#EAB308]" />
                  </div>
                  <span className="text-sm font-medium">{PHONE_DISPLAY}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} data-testid="footer-email"
                  className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors group">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/15 group-hover:bg-[#EAB308]/15 transition-colors shrink-0">
                    <Mail size={15} className="text-[#EAB308]" />
                  </div>
                  <span className="text-sm">{EMAIL}</span>
                </a>
              </li>
              <li>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
                  className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors group">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/15 group-hover:bg-[#EAB308]/15 transition-colors shrink-0">
                    <MessageCircle size={15} className="text-[#EAB308]" />
                  </div>
                  <span className="text-sm">{t('footer.whatsapp')}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#A3A3A3]">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/15 shrink-0">
                  <MapPin size={15} className="text-[#EAB308]" />
                </div>
                <span className="text-sm pt-2">{t('footer_address')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/8 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[#555] text-xs">
            <p>© {new Date().getFullYear()} Taxis Yellow Cab Corsica · {t('footer_copyright')}</p>
            <div className="flex gap-5">
              <Link to="/mentions-legales" className="hover:text-[#A3A3A3] transition-colors" data-testid="footer-legal-link">
                {t('footer.legal')}
              </Link>
              <Link to="/politique-de-confidentialite" className="hover:text-[#A3A3A3] transition-colors" data-testid="footer-privacy-link">
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
